import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import styled from "styled-components/macro";
import { themePalette } from "../../theme";
import { featureDiscoveryColors } from "./featureDiscovery.colors";

export const TourDialog = styled(Dialog)`
  .MuiDialog-container {
    align-items: center;
  }

  && .MuiDialog-paper {
    border-radius: 10px;
    max-width: none;
    width: min(882px, calc(100vw - 32px));
    max-height: calc(100dvh - 32px);
    margin: 16px;
    overflow: hidden;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);

    @media (max-width: 700px) {
      width: calc(100vw - 24px);
      max-height: calc(100dvh - 24px);
      margin: 12px;
    }
  }
`;

export const TourContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  padding: 40px;
  box-sizing: border-box;
  max-height: inherit;
  overflow: auto;

  @media (max-width: 700px) {
    padding: 24px 20px;
  }
`;

export const TourHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 700px) {
    align-items: flex-start;
  }
`;

export const TourTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
  min-width: 0;

  @media (max-width: 700px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
`;

export const TourTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.headerBackground};
  font-family: bold;
  font-size: 24px;
  line-height: 30px;
  letter-spacing: 0;

  @media (max-width: 700px) {
    font-size: 22px;
    line-height: 28px;
  }
`;

export const TourBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 5px;
  box-sizing: border-box;
  background: ${featureDiscoveryColors.badgeYellow};
  color: ${themePalette.black};
  font-family: bold;
  font-size: 14px;
  line-height: 22px;
`;

export const TourCloseButton = styled(IconButton)`
  && {
    flex: 0 0 auto;
    width: 30px;
    height: 30px;
    padding: 0;
    font-size: 16px;
    color: ${({ theme }) => theme.greyMedium};
  }
`;

export const TourContent = styled.div`
  display: grid;
  grid-template-columns: 267px minmax(0, 500px);
  gap: 35px;
  align-items: start;
  justify-content: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 28px;
  }
`;

export const TourTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-height: 375px;

  @media (max-width: 900px) {
    gap: 24px;
    min-height: auto;
  }
`;

export const TourCopy = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.headerBackground};
  font-family: light;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0;

  p {
    margin: 0 0 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-family: bold;
  }
`;

export const TourImage = styled.img`
  align-self: start;
  width: 100%;
  max-width: 500px;
  height: 375px;
  border-radius: 8px;
  object-fit: cover;
  pointer-events: none;

  @media (max-width: 900px) {
    height: auto;
  }
`;

export const TourFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 700px) {
    flex-wrap: wrap;
  }
`;

export const TourProgress = styled.span`
  color: ${themePalette.black};
  font-family: bold;
  font-size: 16px;
  line-height: 24px;
`;

export const TourButton = styled.button<{ $variant: "primary" | "secondary" }>`
  height: 32px;
  padding: 0 16px;
  border-radius: 30px;
  border: 1px solid ${({ theme }) => theme.purpleDark};
  background: ${({ $variant, theme }) =>
    $variant === "primary" ? theme.purpleDark : theme.white};
  color: ${({ $variant, theme }) =>
    $variant === "primary" ? theme.white : theme.purpleDark};
  cursor: pointer;
  font-family: bold;
  font-size: 14px;
  line-height: 14px;
  letter-spacing: 0.14px;
  text-transform: uppercase;

  &:hover {
    background: ${({ $variant, theme }) =>
      $variant === "primary"
        ? theme.purpleMedium
        : featureDiscoveryColors.lavenderTint};
  }
`;

export const HelpTooltipBubble = styled.div`
  width: min(480px, calc(100vw - 32px));
  padding: 28px 32px 30px;
  border-radius: 8px;
  box-sizing: border-box;
  background: ${featureDiscoveryColors.lavenderSurface};
  color: ${themePalette.black};
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 22px solid transparent;
    border-right: 22px solid transparent;
    border-bottom: 22px solid ${featureDiscoveryColors.lavenderSurface};
  }

  @media (max-width: 700px) {
    padding: 28px 24px;
  }
`;

export const HelpTooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
`;

export const HelpTooltipTitle = styled.h3`
  margin: 0 0 20px;
  color: ${themePalette.purpleDark};
  font-family: bold;
  font-size: 28px;
  line-height: 34px;
`;

export const HelpTooltipBody = styled.p`
  margin: 0 0 24px;
  font-family: light;
  font-size: 17px;
  line-height: 26px;
`;

export const HelpTooltipCloseButton = styled.button`
  border: 0;
  background: transparent;
  color: ${themePalette.black};
  cursor: pointer;
  font-family: bold;
  font-size: 28px;
  line-height: 28px;
`;
